/* eslint-disable @typescript-eslint/no-non-null-assertion */

import type {
  ActionConfig,
  ActionReference,
  Display,
  InputDescription,
  IntegrationDescription,
  OutputDescription,
  ValueConfig,
} from "@smartsuite/automation-engine-api";
import {
  Display_ShowRequiredStatus,
  ValueDescription_T,
  unwrapValueConfig,
} from "@smartsuite/automation-engine-api";

import type { Fields } from "@smartsuite/smartsuite-client";
import { t } from "i18next";
import * as R from "remeda";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../types/candidate";
import type {
  DynamicSection,
  DynamicSectionOption,
} from "../../../../types/line";
import { getOutputDescriptions } from "../../../../utils/conditions-coverter.hook";
import {
  ActionLabel,
  FIND_RECORD_LIMIT_ID,
  INPUT_ACTION_NAME_ID,
  Integrations,
  smartSuiteIntegration,
} from "../../../../utils/constants";
import {
  findInputAndPick,
  pickActionName,
  pickValueConfigLimitSort,
  pickValueConfigString,
} from "../../../../utils/inputs";
import type { LoopScope } from "../../../../stores/automation/looping.store";
import { getLoopingSourceActionReference } from "./FieldEditor/FieldEditorRow/fields/utils";
import memoize from "lodash/memoize";

const findTriggerName = (
  integrations: IntegrationDescription[],
  trigger: TriggerCandidate,
): string => {
  return R.pipe(
    integrations,
    R.find((i) => i.integrationId === trigger.triggerReference!.integrationId),
    (integration) => integration?.triggers ?? [],
    R.find((t) => t.triggerId === trigger.triggerReference!.triggerId),
    (t) => t?.label ?? "",
  );
};

export const isCurrentActionInLoop = (
  looping: LoopScope,
  currentAction?: ActionCandidate,
): boolean =>
  looping.loopActions.some((action) => {
    if (
      action.actionReference !== undefined &&
      currentAction?.actionReference !== undefined
    ) {
      return referenceMatches(action.actionReference)(
        currentAction.actionReference,
      );
    }
    return false;
  });

export const convertTriggerToDynamicSection = (
  trigger: TriggerCandidate,
  integrations: IntegrationDescription[],
  generalTypes?: ValueDescription_T[],
  ssTypes?: ValueDescription_T[],
  ssFieldTypes?: Fields[],
  systemAllowedOptions?: string[],
  sourceTypes?: Array<{ sourceType: string; types: ValueDescription_T[] }>,
  excludedTeamFieldOutputSuffixes?: string[],
): DynamicSection | null => {
  if (R.isTruthy(trigger.triggerReference?.triggerId)) {
    return {
      type: "trigger",
      hash: trigger.hash,
      name: findTriggerName(integrations, trigger),
      options: getOutputDescriptions(trigger)
        .filter((output) => {
          if (
            output.smartsuiteFieldType === "teamfield" &&
            excludedTeamFieldOutputSuffixes !== undefined &&
            excludedTeamFieldOutputSuffixes.length > 0
          ) {
            return !excludedTeamFieldOutputSuffixes.some((outputSuffix) =>
              output.outputId.endsWith(outputSuffix),
            );
          }
          if (isSimpleTextOutput(output)) {
            return true;
          }
          if (output.contextSpecific) {
            return systemTypeFilter(systemAllowedOptions, output);
          }
          if (
            (output.sourceFieldType !== undefined ||
              output.value?.sourceType !== undefined) &&
            sourceTypes !== undefined
          ) {
            return sourceTypeFilter(sourceTypes, output);
          }
          if (output.smartsuiteFieldType !== undefined) {
            return ssTypeFilter(
              output.smartsuiteFieldType,
              ssFieldTypes,
              ssTypes,
              output,
            );
          }

          return generalTypeFilter(generalTypes, output);
        })
        .map((output) => ({
          label: output.label,
          outputId: output.outputId,
          icon: output.icon,
          contextSpecific: output.contextSpecific,
          labelSuffix: output.labelSuffix,
          groupFieldId: output.groupFieldId,
          smartsuiteFieldType: output.smartsuiteFieldType,
        })),
    };
  }
  return null;
};

export const ssTypeFilter = (
  ssType: string,
  ssFieldTypes: Fields[] | undefined,
  generalTypes: ValueDescription_T[] | undefined,
  output: OutputDescription,
): boolean => {
  if (ssFieldTypes !== undefined) {
    return (
      ssFieldTypes.includes(ssType as Fields) &&
      generalTypeFilter(generalTypes, output)
    );
  }
  return generalTypeFilter(generalTypes, output);
};

export const generalTypeFilter = (
  types: ValueDescription_T[] | undefined,
  output: OutputDescription,
): boolean => {
  if (types === undefined || output.value?.type === undefined) {
    return true;
  }
  return types.includes(output.value.type);
};

export const systemTypeFilter = (
  types: string[] | undefined,
  output: OutputDescription,
): boolean => {
  if (types === undefined) {
    return true;
  }
  return types.includes(output.outputId);
};

const sourceTypeFilter = (
  sourceTypes: Array<{ sourceType: string; types: ValueDescription_T[] }>,
  output: OutputDescription,
): boolean => {
  const outputType = output.sourceFieldType ?? output.value?.sourceType;
  const fieldAllowed = sourceTypes.find(
    (sourceType) => sourceType.sourceType === outputType,
  );

  return (
    fieldAllowed !== undefined &&
    output.value?.type !== undefined &&
    fieldAllowed.types.includes(output.value.type)
  );
};

export const isValidURL = (url: string): boolean => {
  const res = url.match(
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g,
  );
  return res !== null;
};

export const nameToInitials = (name: string, maxLength = 2): string =>
  name
    .split(" ")
    .map((i) => i.charAt(0))
    .splice(0, maxLength)
    .join("");

export const isRequired = (display?: Display): boolean =>
  display?.showRequiredStatus === Display_ShowRequiredStatus.REQUIRED;

export const getInAppMessageForTrigger = (
  trigger: TriggerCandidate,
): ValueConfig => {
  switch (trigger.description.triggerId) {
    case "created-record":
      return {
        values: [
          {
            value: {
              oneofKind: "string",
              string: t("in-app-message-default-message.created"),
            },
          },
          {
            value: {
              oneofKind: "reference",
              reference: {
                outputId: "field-first_created.by",
                source: {
                  oneofKind: "triggerReference",
                  triggerReference: {
                    triggerId: "created-record",
                    integrationId: smartSuiteIntegration,
                  },
                },
              },
            },
          },
        ],
      };
    case "updated-record":
      return {
        values: [
          {
            value: {
              oneofKind: "string",
              string: t("in-app-message-default-message.updated"),
            },
          },
          {
            value: {
              oneofKind: "reference",
              reference: {
                outputId: "field-last_updated.by",
                source: {
                  oneofKind: "triggerReference",
                  triggerReference: {
                    triggerId: "updated-record",
                    integrationId: smartSuiteIntegration,
                  },
                },
              },
            },
          },
        ],
      };
    case "matches-condition":
    case "schedule":
      return {
        values: [
          {
            value: {
              oneofKind: "string",
              string: t("in-app-message-default-message.matches-condition"),
            },
          },
        ],
      };
  }
  return {
    values: [
      {
        value: {
          oneofKind: "string",
          string: "",
        },
      },
    ],
  };
};

export const getActionName = (
  action: ActionCandidate,
  integrations: IntegrationDescription[],
): string => {
  const actionId = action.actionReference?.actionId;

  if (actionId === ActionLabel.findRecords) {
    return (
      findInputAndPick(
        action.inputs,
        INPUT_ACTION_NAME_ID,
        pickValueConfigString,
      ) ?? ""
    );
  }

  const actionLabel = R.pipe(
    integrations,
    R.find((i) => i.integrationId === action.actionReference?.integrationId),
    (integration) => integration?.actions ?? [],
    R.find((a) => a.actionId === action.actionReference!.actionId),
    (a) => a?.label ?? "",
  );

  // The AI action lets the user name it via its "Action Name" field; surface
  // that name in the outputs picker when set, otherwise fall back to the
  // action's default label.
  if (actionId === ActionLabel.ai) {
    return pickActionName(action.inputs, actionId) ?? actionLabel;
  }

  return actionLabel;
};
export const convertLoopingSourceOptionsToDynamicSections = (
  looping: LoopScope,
  actions: ActionCandidate[],
  integrations: IntegrationDescription[],
  types = [ValueDescription_T.STRING],
  ssFieldTypes?: Fields[],
  systemAllowedOptions?: string[],
): DynamicSection | undefined => {
  if (
    looping.source?.oneofKind === "recordList" &&
    looping.source.recordList.recordList.oneofKind === "actionReference" &&
    looping.source.recordList.recordList.actionReference.actionId ===
      ActionLabel.findRecords
  ) {
    const actionInstanceId =
      looping?.source.recordList.recordList.actionReference.instanceId;
    const sourceAction = actions.find(
      (action) => action.actionReference?.instanceId === actionInstanceId,
    );
    if (sourceAction === undefined) {
      return undefined;
    }
    return {
      type: "action",
      hash: sourceAction.hash,
      hideNamePrefix:
        sourceAction.actionReference?.actionId === ActionLabel.findRecords,
      name: getActionName(sourceAction, integrations),
      options: outputsToOptions(
        getOutputDescriptions(sourceAction),
        systemAllowedOptions,
        types,
        ssFieldTypes,
      ),
    };
  }
};
interface ResponseElement {
  key: string;
  value: ResponseValue;
}

interface ResponseValue {
  type: ValueDescription_T;
  multiple: boolean;
  elements?: ResponseElements;
}

interface ResponseElements {
  elements: {
    oneofKind: "listElements" | "keyValueElements";
    listElements?: {
      type: ValueDescription_T;
      multiple: boolean;
      elements: ResponseElements;
    };
    keyValueElements?: {
      elements: ResponseElement[];
    };
  };
}

interface FlatOption {
  key: string;
  type: ValueDescription_T;
}

interface ValueDescription {
  type: ValueDescription_T;
  multiple: boolean;
  elements?: ResponseElements;
  key?: string;
}

/**
 * Flattens nested response content structure into a simple array of options
 * @param content - Response content structure to flatten
 * @param isNestedList - Flag to indicate if processing nested list
 * @returns Array of flattened options
 */
export const flattenResponseContent = (
  content: ValueDescription,
  isNestedList = false,
): FlatOption[] => {
  const result: FlatOption[] = [];

  // If this is a nested LIST and not the first occurrence, return it as is
  if (isNestedList && content.type === ValueDescription_T.LIST) {
    return [
      {
        key: content.key ?? "",
        type: ValueDescription_T.LIST,
      },
    ];
  }

  // Process LIST type (type 24)
  if (content.type === ValueDescription_T.LIST) {
    const elements =
      content.elements?.elements?.oneofKind === "listElements"
        ? content.elements.elements.listElements
        : null;
    if (elements) {
      return flattenResponseContent(elements, false);
    }
  }

  // Process KEY_VALUE type (type 23)
  if (content.type === ValueDescription_T.KEY_VALUE) {
    const keyValueElements =
      content.elements?.elements?.oneofKind === "keyValueElements"
        ? content.elements.elements.keyValueElements?.elements
        : null;

    if (keyValueElements) {
      keyValueElements.forEach((element: ResponseElement) => {
        if (element.value.type === ValueDescription_T.KEY_VALUE) {
          // Recursively process nested KEY_VALUE structures
          const nestedValue = {
            ...element.value,
            key: element.key,
          };
          result.push(...flattenResponseContent(nestedValue, false));
        } else if (element.value.type === ValueDescription_T.LIST) {
          // For LIST type, just add the key without further processing
          result.push({
            key: element.key,
            type: ValueDescription_T.LIST,
          });
        } else {
          // For simple types (STRING, NUMBER, etc), add them directly
          result.push({
            key: element.key,
            type: element.value.type,
          });
        }
      });
    }
  }

  return result;
};

export const getCurrentItemOutputId = (input: string): string => {
  const pipeSeparatorIndex = input.indexOf("|");

  if (pipeSeparatorIndex !== -1) {
    return `currentItem${input.slice(pipeSeparatorIndex)}`;
  }

  const dotSeparatorIndex = input.indexOf(".");

  if (dotSeparatorIndex === -1) {
    return "currentItem";
  }

  return `currentItem${input.slice(dotSeparatorIndex)}`;
};

const getIconByType = (type: ValueDescription_T): string => {
  switch (type) {
    case ValueDescription_T.STRING:
      return "text";
    case ValueDescription_T.NUMBER:
      return "number";
    case ValueDescription_T.BOOL:
      return "yes-and-no";
    case ValueDescription_T.KEY_VALUE:
      return "object";
    case ValueDescription_T.LIST:
      return "array";
    default:
      return "object";
  }
};

const convertOutputToDynamicSectionOption = (
  looping: LoopScope,
  actions: ActionCandidate[],
  trigger: TriggerCandidate,
): DynamicSection | null => {
  const loopingSource = looping.source;

  if (loopingSource?.oneofKind === "recordList") {
    return null;
  }

  const webhookTriggerId =
    looping.source?.oneofKind === "outputReference" &&
    looping.source?.outputReference.source.oneofKind === "triggerReference"
      ? looping.source?.outputReference.source.triggerReference.triggerId
      : null;

  const webhookActionInstanceId =
    looping.source?.oneofKind === "outputReference" &&
    looping.source?.outputReference.source.oneofKind === "actionReference"
      ? looping.source?.outputReference.source.actionReference.instanceId
      : null;

  const outputReference =
    looping.source?.oneofKind === "outputReference"
      ? looping.source?.outputReference
      : null;

  if (!outputReference) {
    return null;
  }

  if (!webhookTriggerId && !outputReference) {
    return null;
  }

  const webhookAction = webhookActionInstanceId
    ? actions.find(
        (action) =>
          action.actionReference?.instanceId === webhookActionInstanceId,
      )
    : trigger;

  const responseContent = webhookAction?.description.outputs.find(
    (output) => output.outputId === outputReference.outputId,
  )?.value;

  if (!responseContent) {
    return null;
  }

  const flattenedContent = flattenResponseContent(
    responseContent as ValueDescription,
  );

  return {
    type: webhookActionInstanceId ? "action" : "trigger",
    hash: webhookAction?.hash ?? "",
    name: t("general.current-item"),
    options: flattenedContent.map((option) => {
      return {
        label: option.key.split("|").pop() ?? "",
        outputId: getCurrentItemOutputId(option.key),
        icon: getIconByType(option.type),
        contextSpecific: false,
      };
    }),
  };
};

export const memoizedConvertOutputToDynamicSectionOption = memoize(
  (
    loopingStore: LoopScope,
    actions: ActionCandidate[],
    trigger: TriggerCandidate,
  ) => convertOutputToDynamicSectionOption(loopingStore, actions, trigger),
  (
    loopingStore: LoopScope,
    actions: ActionCandidate[],
    trigger: TriggerCandidate,
  ) => {
    return JSON.stringify({
      loopingSource: loopingStore.source,
      actionsHashes: actions.map((a) => a.hash),
      outputs: actions.map((a) => a.description.outputs),
      trigger: trigger?.description.outputs,
    });
  },
);

const outputsToOptions = (
  outputs: OutputDescription[],
  systemAllowedOptions?: string[],
  types = [ValueDescription_T.STRING],
  ssFieldTypes?: Fields[],
  excludedTeamFieldOutputSuffixes?: string[],
): DynamicSectionOption[] => {
  return outputs
    .filter((output) => {
      if (
        output.smartsuiteFieldType === "teamfield" &&
        excludedTeamFieldOutputSuffixes !== undefined &&
        excludedTeamFieldOutputSuffixes.length > 0
      ) {
        return !excludedTeamFieldOutputSuffixes.some((outputSuffix) =>
          output.outputId.endsWith(outputSuffix),
        );
      }
      if (isSimpleTextOutput(output)) {
        return true;
      }
      if (output.contextSpecific) {
        return systemTypeFilter(systemAllowedOptions, output);
      }
      let pass =
        output.value?.type === undefined
          ? true
          : types.includes(output.value?.type);
      if (ssFieldTypes !== undefined) {
        pass =
          output.smartsuiteFieldType === undefined ||
          (ssFieldTypes.includes(output.smartsuiteFieldType as Fields) &&
            (output.value?.type === undefined ||
              types.includes(output.value?.type)));
      }

      return pass;
    })
    .map((output) => ({
      label: output.label,
      outputId: output.outputId,
      icon: output.icon,
      contextSpecific: output.contextSpecific,
      labelSuffix: output.labelSuffix,
      groupFieldId: output.groupFieldId,
      smartsuiteFieldType: output.smartsuiteFieldType,
    }));
};
export const convertActionsToDynamicSections = (
  actions: ActionCandidate[],
  /** The action this editor belongs to, used as the upper boundary of the
   * offered sources. Conditional Logic branch conditions have no owning action
   * -- they pass `undefined` together with an `actions` list already scoped to
   * the block's own execution path, so nothing is sliced off. */
  currentAction: ActionCandidate | undefined,
  integrations: IntegrationDescription[],
  types = [ValueDescription_T.STRING],
  ssFieldTypes?: Fields[],
  systemAllowedOptions?: string[],
  looping?: LoopScope,
  verifySupportedAction: (
    action: ActionConfig,
    actions: ActionConfig[],
    currentAction?: ActionCandidate,
    looping?: LoopScope,
  ) => boolean = isSupportedOutput,
  excludedTeamFieldOutputSuffixes?: string[],
): DynamicSection[] => {
  const loopingSourceFindActionId =
    looping?.source?.oneofKind === "recordList" &&
    looping.source.recordList.recordList.oneofKind === "actionReference"
      ? looping.source.recordList.recordList.actionReference.instanceId
      : undefined;

  return R.pipe(
    actions,
    R.takeWhile(
      (action) =>
        currentAction === undefined || action.hash !== currentAction.hash,
    ),
    R.filter((action) => {
      if (action.hash === currentAction?.hash) {
        return false;
      }
      const isSupported = verifySupportedAction(
        action,
        actions,
        currentAction,
        looping,
      );
      if (!isSupported) {
        return false;
      }
      if (action.actionReference === undefined) {
        return false;
      }
      return true;
    }),
    R.filter((action) => {
      return action.actionReference?.instanceId !== loopingSourceFindActionId;
    }),
    R.map((action) => {
      return {
        type: "action",
        hash: action.hash,
        hideNamePrefix:
          action.actionReference?.actionId === ActionLabel.findRecords ||
          (action.actionReference?.actionId === ActionLabel.ai &&
            pickActionName(action.inputs, ActionLabel.ai) !== undefined),
        name: getActionName(action, integrations),
        options: outputsToOptions(
          getOutputDescriptions(action),
          systemAllowedOptions,
          types,
          ssFieldTypes,
          excludedTeamFieldOutputSuffixes,
        ),
      };
    }),
  );
};

export const shouldShowForNestedFields = (
  input: InputCandidate,
  inputs: InputCandidate[],
): boolean => {
  const openInputSameGroup = inputs.find(
    (i) =>
      i.isOpen === true &&
      fieldIdWithoutSuffix(i) === fieldIdWithoutSuffix(input),
  );
  if (openInputSameGroup) {
    return !(
      (isFieldWithSuffix(openInputSameGroup) && !isFieldWithSuffix(input)) ||
      (!isFieldWithSuffix(openInputSameGroup) && isFieldWithSuffix(input))
    );
  }
  return true;
};

const isFieldWithSuffix = (input: InputCandidate): boolean => {
  return input.inputId.includes(".");
};

const fieldIdWithoutSuffix = (input: InputCandidate): string => {
  return input.inputId.split(".")[0];
};

export const isLoopingSourceOptions = (
  action: ActionConfig,
  currentAction?: ActionCandidate,
  looping?: LoopScope,
): boolean => {
  if (looping === undefined) {
    return false;
  }

  // Check if the action is the source of the loop
  const loopingSourceActionReference = getLoopingSourceActionReference(looping);

  const isActionSource =
    loopingSourceActionReference !== undefined &&
    referenceMatches(loopingSourceActionReference)(action.actionReference!);

  return isCurrentActionInLoop(looping, currentAction) || isActionSource;
};

export const isSupportedOutput = (
  action: ActionConfig,
  actions: ActionConfig[],
  currentAction?: ActionCandidate,
  looping?: LoopScope,
  /** `isFindForAiAction` widens the AI action to any Find, single or multiple.
   * A file input (the AI action's Attachments) cannot take a multiple Find:
   * its output is a list of records, not one attachment. Single Find still
   * passes through `isFindRecordsOne`. */
  allowMultipleFindForAi = true,
): boolean => {
  return (
    isLoopingSourceOptions(action, currentAction, looping) ||
    isFindRecordsOne(action) ||
    (allowMultipleFindForAi && isFindForAiAction(action, currentAction)) ||
    sameRecordList(action, currentAction) ||
    isNonLoopOutputs(action, actions)
  );
};

/** `isSupportedOutput` for inputs that hold files. */
export const isSupportedOutputForFile: typeof isSupportedOutput = (
  action,
  actions,
  currentAction,
  looping,
) => isSupportedOutput(action, actions, currentAction, looping, false);

const isFindForAiAction = (
  action: ActionConfig,
  currentAction?: ActionCandidate,
): boolean =>
  action.actionReference?.actionId === "find-records" &&
  currentAction !== undefined &&
  currentAction.actionReference?.actionId === ActionLabel.ai;

const isNonLoopOutputs = (
  action: ActionConfig,
  actions: ActionConfig[],
): boolean => {
  if (action.actionReference?.actionId === "find-records") {
    return false;
  }
  if (action.recordList !== undefined) {
    if (action.recordList.recordList.oneofKind !== "actionReference") {
      return true;
    } else {
      if (
        action.recordList.recordList.oneofKind === "actionReference" &&
        action.recordList.recordList.actionReference.actionId === "find-records"
      ) {
        const instanceId =
          action.recordList.recordList.actionReference.instanceId;
        const usedAction = actions.find(
          (a) => a.actionReference?.instanceId === instanceId,
        );
        if (usedAction !== undefined && isFindRecordsOne(usedAction)) {
          return true;
        }
        return false;
      }
    }
    return false;
  }
  return true;
};

export const isFindRecordsOne = (action: ActionConfig): boolean => {
  if (action?.actionReference?.actionId === "find-records") {
    const limitsInput = findInputAndPick(
      action.inputs,
      FIND_RECORD_LIMIT_ID,
      pickValueConfigLimitSort,
    );

    if (limitsInput !== undefined && limitsInput.limit === 1) {
      return true;
    }
  }
  return false;
};

/** The list a step was given, when an earlier step made it. */
const listSourceOf = (action: ActionConfig | undefined): number | undefined =>
  action?.recordList?.recordList.oneofKind === "actionReference"
    ? action.recordList.recordList.actionReference.instanceId
    : undefined;

/**
 * Whether the step doing the reading was run over the same records as the one
 * it reads.
 *
 * A step given a list is run once per record of it, and the engine resolves a
 * reference between two such steps at the record the reading one is running
 * for. So either the reader runs over the list the other step made, or the two
 * were given the same list. Anything else reads the first record's result
 * whichever record it is for, which is what the engine does and why it is not
 * offered.
 */
const sameRecordList = (
  action: ActionConfig,
  currentAction?: ActionCandidate,
): boolean => {
  const readOver = listSourceOf(currentAction);
  return (
    readOver !== undefined &&
    (readOver === action.actionReference?.instanceId ||
      readOver === listSourceOf(action))
  );
};

export const referenceMatches =
  (a: ActionReference) =>
  (b: ActionReference): boolean => {
    return (
      a.actionId === b.actionId &&
      a.integrationId === b.integrationId &&
      a.instanceId === b.instanceId
    );
  };

export const shouldHideTeams = (
  description: InputDescription | undefined,
): boolean => {
  // TODO add hide_teams to MemberEditorDescription
  return description?.inputId === "comment-assigned-to";
};

export const getDescriptionTypeByValue = (
  value?: ValueConfig,
): ValueDescription_T => {
  if (value === undefined) {
    return ValueDescription_T.ANY;
  }
  const unwrapped = unwrapValueConfig(value);
  switch (unwrapped?.values[0]?.value?.oneofKind) {
    case "keyValue":
      return ValueDescription_T.KEY_VALUE;
    case "list":
      return ValueDescription_T.LIST;
    case "string":
      return ValueDescription_T.STRING;
    case "bool":
      return ValueDescription_T.BOOL;
    case "number":
      return ValueDescription_T.NUMBER;
    default:
      return ValueDescription_T.ANY;
  }
};

export const formatValueConfigToStructure = (
  value?: ValueConfig,
  key = "",
  index?: number,
): string[] => {
  if (value === undefined) {
    return [];
  }
  value = unwrapValueConfig(value);

  return value.values.flatMap((v) => {
    switch (v.value.oneofKind) {
      case "string":
        return `${getKey(key, index)}${v.value.string}`;
      case "number":
        return `${getKey(key, index)}${v.value.number}`;
      case "bool":
        return `${getKey(key, index)}${v.value.bool ? "true" : "false"}`;
      case "keyValue": {
        let currentKey =
          v.value.keyValue.key?.values[0]?.value?.oneofKind === "string"
            ? v.value.keyValue.key?.values[0].value.string
            : "";
        if (key !== "") {
          currentKey = `${key}.${currentKey}`;
        }
        return formatValueConfigToStructure(v.value.keyValue.value, currentKey);
      }
      case "list": {
        const list = v.value.list.values.flatMap((v) =>
          formatValueConfigToStructure(v),
        );
        return `${getKey(key, index)}Array of [${list.join(", ")}]`;
      }
      default:
        return `${getKey(key, index)}Unknown`;
    }
  });
};

const getKey = (key: string, index?: number): string => {
  if (key === "") {
    return "";
  }
  return index !== undefined ? `${key}[${index}]: ` : `${key}: `;
};

export const isSimpleTextOutput = (output: OutputDescription): boolean =>
  output.value?.type === ValueDescription_T.STRING &&
  !output.contextSpecific &&
  !output.outputId.includes(".") &&
  output.smartsuiteFieldType === "textfield";

export const isWebhookTextOutput = (
  trigger: TriggerCandidate,
  output: OutputDescription,
): boolean =>
  output.value?.type === ValueDescription_T.STRING &&
  !output.contextSpecific &&
  trigger.triggerReference?.integrationId === Integrations.webhooks;
